import { Injectable, Injector } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import {
  PhoneIntel,
  SpouseInfo,
} from '@app/forms/subscription/definition/interface/subscription';
import * as _ from 'lodash';
import FormGroupArray from '../../services/FormGroupArray';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  protected countries: any[] = [];
  constructor(
    protected formConstructor: PrFormConstructor,
    protected injector: Injector,
  ) {
    super(formConstructor);
    injector
      .get(ReferenceDao)
      .getCountries()
      .subscribe(countries => {
        this.countries = countries;
      });
  }

  generateForm(entity: SpouseInfo, option?) {
    let options: AbstractControlOptions = {};

    const formBuilderGroup = this.formBuilder.group(
      {
        ownerId: [entity?.ownerId],
        userFirstName: [entity?.userFirstName || null],
        userLastName: [entity?.userLastName || null],
        userSpouseFirstName: [
          entity?.userSpouseFirstName || null,
          Validators.required,
        ],
        userSpouseLastName: [
          entity?.userSpouseLastName || null,
          Validators.required,
        ],
        userSpouseBirthName: [entity?.userSpouseBirthName || null],
        userSpouseBirthDate: [
          entity?.userSpouseBirthDate || null,
          Validators.required,
        ],
        userSpouseBirthCity: [
          entity?.userSpouseBirthCity || null,
          Validators.required,
        ],
        userSpouseEmail: [
          entity?.userSpouseEmail || null,
          [Validators.required, Validators.email],
        ],
        spouseAddress: this.formBuilder.group({
          street: [
            entity && entity.userSpouseAddressStreet
              ? entity.userSpouseAddressStreet
              : null,
            Validators.required,
          ],
          zipCode: [
            entity && entity.userSpouseAddressZipCode
              ? entity.userSpouseAddressZipCode
              : null,
          ],
          city: [
            entity && entity.userSpouseAddressCity
              ? entity.userSpouseAddressCity.toUpperCase()
              : null,
            Validators.required,
          ],
          country: [
            entity && entity.userSpouseAddressCountry
              ? {
                  id: this.findCountryId(entity.userSpouseAddressCountry),
                  value: this.findCountryValue(entity.userSpouseAddressCountry),
                  valueGb: this.findCountryValueGb(
                    entity.userSpouseAddressCountry,
                  ),
                }
              : null,
            Validators.required,
          ],
        }),
        userSpouseContactPhones: this.formBuilder.array([this.formGroup()]),
      },
      options,
    );
    entity?.userSpouseContactPhones?.length > 0 &&
      formBuilderGroup.setControl(
        'userSpouseContactPhones',
        this.populate(entity),
      );
    return formBuilderGroup;
  }

  findCountryId(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)?.id ||
      country
    );
  }

  findCountryValue(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)
        ?.value || country
    );
  }

  findCountryValueGb(country: string): string {
    if (!country) {
      return country;
    }
    return (
      this.countries.find(c => c.id === country || c.value === country)
        ?.valueGb || country
    );
  }

  generate(entity: SpouseInfo[], option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
    });
  }

  get owners() {
    return this.form.get('owners') as UntypedFormArray;
  }

  populate(data: SpouseInfo) {
    let formArray = this.formBuilder.array([this.formGroup()]);
    if (data && data.userSpouseContactPhones) {
      formArray.clear();
      data.userSpouseContactPhones.forEach(item => {
        formArray.push(
          this.formBuilder.group({
            phone: [item, [Validators.required, Validators.minLength(9)]],
          }),
        );
      });
    }
    return formArray;
  }
  formGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      phone: ['', [Validators.required, Validators.minLength(9)]],
    });
  }
  extract() {
    const formValue = _.cloneDeep(this.form.value);
    const owners = formValue.owners.map(owner => {
      const address = this.transformSpouseAddress(owner.spouseAddress);
      const phones = this.transformTelephone(owner.userSpouseContactPhones);
      return {
        ..._.omit(owner, ['spouseAddress']),
        ...address,
        userSpouseContactPhones: phones,
      };
    });
    return owners;
  }
  transformSpouseAddress(originalAddress: {
    street: string;
    zipCode: string;
    city: string;
    country: { id: string; valueGb: string; value: string };
  }) {
    const address = originalAddress
      ? (({ street, city, zipCode, country, ...address }) => ({
          ...address,
          userSpouseAddressStreet: street || '',
          userSpouseAddressCity: city || '',
          userSpouseAddressZipCode: zipCode || '',
          userSpouseAddressCountry: country?.id || country || '',
        }))(originalAddress)
      : {};
    return address;
  }
  transformTelephone(telephone: PhoneIntel[]) {
    return telephone
      .map(item => {
        return typeof item?.phone === 'string'
          ? item.phone
          : item?.phone?.e164Number;
      })
      .filter(phone => !!phone);
  }
}
